import set from "@rill/set";
import React from "react";
import SvgIcon from "../../../parts/SvgIcon";
import SettingsNav from "../../../parts/settings-nav";
import ToolTip from "../../../parts/tooltip";

const PROVIDERS = [
  { key: "zoom", label: "Zoom" },
  { key: "google_meet", label: "Google Meet" },
];

const TOOLTIP =
  "Connect Zoom or Google Meet to automatically generate a unique meeting " +
  "link whenever an online session is booked. Meetings are hosted by the " +
  "connected account.";

/**
 * Why a connection can't create meeting links (the API's `problem`, or the
 * `reason` of a failed connect), as shown to the coach.
 */
function problemText(problem, label) {
  if (problem === "missing_scope") {
    return (
      `${label} needs access to your calendar to create meeting links. ` +
      "Connect again and allow calendar access."
    );
  }
  if (problem === "auth_failed") {
    return `Connection lost. Reconnect ${label} to create meeting links.`;
  }
  return null;
}

const providerLabel = (key) =>
  PROVIDERS.find((p) => p.key === key)?.label ?? "The meeting provider";

function View() {
  const [status, setStatus] = React.useState(null);
  const [busy, setBusy] = React.useState(null);
  const [error, setError] = React.useState(null);

  const load = React.useCallback(async () => {
    try {
      const res = await fetch("/actions/integrations/meeting/status", {
        credentials: "same-origin",
      });
      if (!res.ok) throw new Error("Couldn't load connection status.");
      setStatus(await res.json());
    } catch (e) {
      setError(e.message || "Couldn't load connection status.");
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  React.useEffect(() => {
    const handle = (payload) => {
      if (payload?.source !== "msb-meeting-integrations") return;
      if (payload.error) {
        setError(
          problemText(payload.reason, providerLabel(payload.provider)) ??
            "Couldn't connect. Please try again."
        );
      }
      load();
    };

    let channel = null;
    if (typeof BroadcastChannel !== "undefined") {
      channel = new BroadcastChannel("msb-meeting-integrations");
      channel.onmessage = (e) => handle(e.data);
    }

    // Fallback for browsers without BroadcastChannel (e.g. older Safari).
    const onStorage = (e) => {
      if (e.key !== "msb-meeting-integrations" || !e.newValue) return;
      try {
        handle(JSON.parse(e.newValue));
      } catch {}
    };
    window.addEventListener("storage", onStorage);

    return () => {
      if (channel) channel.close();
      window.removeEventListener("storage", onStorage);
    };
  }, [load]);

  const openConnect = React.useCallback((provider) => {
    setError(null);
    const url = `/actions/integrations/meeting/${provider}/connect`;

    const win = window.open(url, "_blank");
    if (!win) window.location.assign(url);
  }, []);

  const disconnect = async (provider) => {
    setBusy(provider);
    setError(null);
    try {
      const res = await fetch(
        `/actions/integrations/meeting/${provider}/disconnect`,
        { method: "DELETE", credentials: "same-origin" }
      );
      if (!res.ok && res.status !== 204) {
        throw new Error("Couldn't disconnect.");
      }
      await load();
    } catch (e) {
      setError(e.message || "Couldn't disconnect.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <main className="settings">
      <SettingsNav classNames={"mobile-hidden"} />
      <section className="preferences-wrapper">
        <h3 className="force-mobile-hidden">Integrations</h3>
        <div className="flex items-center gap-2 mb-4">
          <p className="s1">Meeting providers</p>
          <ToolTip
            id="meeting-integrations-tip"
            hintClassNames="tooltip-solid"
            body={TOOLTIP}
          >
            <SvgIcon
              className="cursor-pointer"
              src="/img/new-icons/question-circle.svg"
              alt="About meeting integrations"
            />
          </ToolTip>
        </div>

        {error && (
          <p className="create-class-modal-error" role="alert">
            {error}
          </p>
        )}

        <form onSubmit={(e) => e.preventDefault()}>
          {PROVIDERS.map(({ key, label }) => {
            const conn = status?.[key];
            const connected = Boolean(conn?.connected);
            return (
              <div key={key} className="row">
                <p>{label}</p>
                <div
                  className="action-buttons"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "1.2rem",
                  }}
                >
                  {connected ? (
                    <>
                      <span style={{ opacity: 0.7 }}>
                        {conn.email ? `Connected · ${conn.email}` : "Connected"}
                      </span>
                      <button
                        type="button"
                        className="settings-integration-btn is-disconnect"
                        disabled={busy === key}
                        onClick={() => disconnect(key)}
                      >
                        {busy === key ? "Disconnecting…" : "Disconnect"}
                      </button>
                    </>
                  ) : status === null ? (
                    <span style={{ opacity: 0.7 }}>Checking…</span>
                  ) : (
                    <>
                      {conn?.problem && (
                        <span style={{ opacity: 0.7 }}>
                          {problemText(conn.problem, label)}
                        </span>
                      )}
                      <a
                        className="settings-integration-btn is-connect"
                        href={`/actions/integrations/meeting/${key}/connect`}
                        target="_blank"
                        rel="opener"
                        onClick={(e) => {
                          e.preventDefault();
                          openConnect(key);
                        }}
                      >
                        <span>Connect</span>
                      </a>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </form>
      </section>
    </main>
  );
}

const MeetingIntegrationsSettings = (app) => {
  app.get("/integrations", set("heading", "Integrations"), ({ res }) => {
    res.body = <View />;
  });
};

export default MeetingIntegrationsSettings;
